/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { Ref, useState } from 'preact/hooks';
import { Property } from 'csstype';
import { classNames } from '../utils/UNSAFE_classNames';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { getResolvedTimeAxesLayout } from '../PRIVATE_TimeComponent/utils/timeAxisLayoutUtils';
import { timeAxisDataBodyStyles } from './themes/GanttStyles.css';
import {
  OnTimeComponentViewportChange,
  TimeComponentZoomParameters
} from '../PRIVATE_TimeComponent/timeComponent.types';
import { getDefaultDateFormatter, getISOString } from '../PRIVATE_TimeComponent/utils/timeUtils';
import {
  getEffectTaskObjs,
  getResolvedViewportLayout,
  type ViewportLayout,
  type getRowDataLayout
} from './utils/dataLayoutUtils';
import { useGanttPanZoomGestures } from './hooks/useGanttPanZoomGestures';
import type {
  GanttCurrentTask,
  GanttProps,
  GanttRowData,
  GanttSelection as GanttSelectionProps,
  GanttTaskData,
  GanttTaskHover,
  OnGanttScrollPositionChange
} from './gantt.types';
import { useGanttTaskPointerGestures } from './hooks/useGanttTaskPointerGestures';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useGanttTaskDatatip } from './hooks/useGanttTaskDatatip';

type GetTimeAxesLayout = ReturnType<typeof getResolvedTimeAxesLayout>;
type MajorAxisLayout = Omit<
  NonNullable<ReturnType<GetTimeAxesLayout>['majorAxis']>,
  'gridTicksPos'
>;
type MinorAxisLayout = Omit<ReturnType<GetTimeAxesLayout>['minorAxis'], 'gridTicksPos'>;

type Props<K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>> = {
  innerRef: Ref<HTMLDivElement>;
  isRTL: boolean;
  scrollPosition: number;
  onScrollPositionChange?: OnGanttScrollPositionChange;
  isViewportDefined: boolean;
  onViewportChange?: OnTimeComponentViewportChange;
  zoomParameters?: TimeComponentZoomParameters;
  zooming: 'on' | 'off';
  getTimeAxesLayout: ReturnType<typeof getResolvedTimeAxesLayout>;
  getResolvedViewportLayout: typeof getResolvedViewportLayout<K1, K2, D1, D2>;
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
  rowAxisSeparator: boolean;
  datatip?: GanttProps<K2, D1, D2>['datatip'];
  majorAxis: (layout: MajorAxisLayout) => ComponentChildren;
  minorAxis: (layout: MinorAxisLayout) => ComponentChildren;
  dataBody: (layout: {
    width: number;
    height: number;
    viewportLayout: ViewportLayout<K1, K2, D1, D2>;
    minorAxisLayout: MinorAxisLayout;
    effectsLayout: ReturnType<typeof getEffectTaskObjs<K2, D2>>;
    dateFormatter: (date: string) => string;
    ariaActiveTask?: { dataId: K2; ariaId: string; type: 'task' };
  }) => ComponentChildren;
  currentTaskInteraction: 'hover' | 'focus' | 'none';
  ariaActive?: { dataId: K2; ariaId: string; type: 'task' };
} & GanttSelectionProps<K2, D2> &
  GanttTaskHover<K2> &
  GanttCurrentTask<K2>;

export function GanttTimeAxisDataBody<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  innerRef,
  isRTL,
  scrollPosition,
  onScrollPositionChange,
  isViewportDefined,
  onViewportChange,
  zoomParameters,
  zooming,
  selectedIds,
  selectionMode,
  onSelectionChange,
  hoveredTaskId,
  onTaskHoverChange,
  currentTask,
  onCurrentTaskChange,
  currentTaskInteraction,
  ariaActive,
  getTimeAxesLayout,
  getResolvedViewportLayout,
  rowDataLayout,
  rowAxisSeparator,
  datatip,
  majorAxis,
  minorAxis,
  dataBody
}: Props<K1, K2, D1, D2>) {
  if (!isViewportDefined) {
    return (
      <TrackResizeContainer width="100%" height="100%">
        {(width) => {
          const timeAxesLayout = getTimeAxesLayout(width);
          const {
            majorAxis: majorAxisLayout,
            minorAxis: minorAxisLayout,
            viewportStartTime,
            viewportEndTime
          } = timeAxesLayout;
          const majorAxisScale =
            majorAxisLayout && majorAxisLayout.zoomOrder[majorAxisLayout.zoomOrderIndex];
          const minorAxisScale = minorAxisLayout.zoomOrder[minorAxisLayout.zoomOrderIndex];

          onViewportChange?.({
            viewportStart: getISOString(viewportStartTime),
            viewportEnd: getISOString(viewportEndTime),
            majorAxisScale,
            minorAxisScale
          });
          return null;
        }}
      </TrackResizeContainer>
    );
  }
  return (
    <TrackResizeContainer width="100%" height="100%">
      {function _(width, height) {
        const timeAxesLayout = getTimeAxesLayout(width);
        const {
          position,
          majorAxis: majorAxisLayout,
          minorAxis: minorAxisLayout,
          viewportStartTime,
          viewportEndTime,
          startTime,
          endTime
        } = timeAxesLayout;
        const majorAxisScale =
          majorAxisLayout && majorAxisLayout.zoomOrder[majorAxisLayout.zoomOrderIndex];
        const minorAxisScale = minorAxisLayout.zoomOrder[minorAxisLayout.zoomOrderIndex];

        const majorAxisHeight = majorAxisLayout ? majorAxisLayout.height : 0;
        const { height: minorAxisHeight } = minorAxisLayout;
        const dataBodyHeight = height - (majorAxisHeight + minorAxisHeight);

        const viewportLayout = getResolvedViewportLayout(
          rowDataLayout,
          viewportStartTime,
          viewportEndTime,
          scrollPosition,
          width,
          dataBodyHeight,
          isRTL
        );
        const effectsLayout = getEffectTaskObjs(
          {
            selectionProps: { selectedIds, selectionMode },
            hoverProps: { hoveredTaskId },
            currentProps: { currentTask }
          },
          viewportLayout.idTaskObjMap
        );
        const defaultDateFormatter = getDefaultDateFormatter(minorAxisScale, viewportStartTime);

        const majorAxisComp = majorAxisLayout && majorAxis(majorAxisLayout);
        const minorAxisComp = minorAxis(minorAxisLayout);
        const dataBodyComp = dataBody({
          width,
          height: dataBodyHeight,
          viewportLayout,
          minorAxisLayout,
          effectsLayout,
          dateFormatter: defaultDateFormatter,
          ariaActiveTask: ariaActive
        });

        const layout = {
          top: {
            components: [majorAxisComp, minorAxisComp, dataBodyComp],
            dataBodyPos: height - dataBodyHeight
          },
          bottom: {
            components: [dataBodyComp, minorAxisComp, majorAxisComp],
            dataBodyPos: 0
          }
        };

        // Temporary; this will be a Gantt prop when we add marquee selection support
        const dragMode = 'pan';

        // TODO: think about a better cursor management system
        const [cursor, setCursor] = useState<Property.Cursor>();
        const onCursorChange = (detail: {
          cursor: 'grabbing' | 'grabbingUnset' | 'pointer' | 'pointerUnset';
        }) =>
          setCursor((prevCursor) => {
            if (prevCursor === undefined) return detail.cursor;
            if (prevCursor === 'grabbing' && detail.cursor !== 'grabbingUnset') return 'grabbing';
            if (detail.cursor === 'grabbingUnset' || detail.cursor === 'pointerUnset')
              return undefined;
            return detail.cursor;
          });

        const { gestureProps: panZoomGestureProps } = useGanttPanZoomGestures({
          captureTargetRef: innerRef,
          width,
          height,
          viewportStartTime,
          viewportEndTime,
          majorAxisScale,
          minorAxisScale,
          startTime,
          endTime,
          zoomParameters: zoomParameters!,
          scrollPosition,
          dataBodyHeight,
          totalRowsHeight: rowDataLayout.totalRowsHeight,
          viewportLayout,
          onViewportChange: (args) => {
            if (onViewportChange) {
              onViewportChange(args);
              // TODO: Evaluate whether it's okay to clear hover behavior on pan and zoom
              onTaskHoverChange({ id: undefined });
            }
          },
          onScrollPositionChange: (args) => {
            if (onScrollPositionChange) {
              onScrollPositionChange(args);
              // TODO: Evaluate whether it's okay to clear hover behavior on pan
              onTaskHoverChange({ id: undefined });
            }
          },
          onCursorChange,
          isPanDisabled: dragMode !== 'pan',
          isZoomDisabled: zooming === 'off'
        });

        const dataBodyRegion = {
          x: 0,
          y: layout[position].dataBodyPos,
          w: width,
          h: dataBodyHeight
        };
        const { gestureProps: taskGestureProps, selectionContent } = useGanttTaskPointerGestures({
          viewportLayout,
          viewportYOffset: scrollPosition - layout[position].dataBodyPos,
          dataBodyRegion,
          viewportStartTime,
          viewportEndTime,
          width,
          isRTL,
          selectionProps: { selectedIds, selectionMode, onSelectionChange },
          hoverProps: { hoveredTaskId, onTaskHoverChange },
          currentProps: { currentTask, onCurrentTaskChange },
          onCursorChange
        });

        const { datatipContent, datatipProps } = useGanttTaskDatatip<K1, K2, D1, D2>({
          datatip,
          rowData: viewportLayout.rowData,
          dateFormatter: defaultDateFormatter,
          hoveredTaskObj: hoveredTaskId && viewportLayout.idTaskObjMap.get(hoveredTaskId),
          focusedTaskObj: effectsLayout.focusedTaskObj,
          currentTaskInteraction,
          dataBodyRegion,
          targetRef: innerRef
        });

        const gestureProps = mergeProps(panZoomGestureProps, taskGestureProps, datatipProps);

        return (
          <div
            ref={innerRef}
            {...gestureProps}
            class={classNames([
              timeAxisDataBodyStyles.base,
              dragMode === 'pan' && timeAxisDataBodyStyles.pannable,
              isRTL
                ? rowAxisSeparator && timeAxisDataBodyStyles.withRowAxisSeparatorRight
                : rowAxisSeparator && timeAxisDataBodyStyles.withRowAxisSeparatorLeft
            ])}
            style={{ cursor }}>
            {layout[position].components}
            {datatipContent}
            {selectionContent}
          </div>
        );
      }}
    </TrackResizeContainer>
  );
}
